/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon';
import BoardIcon from '@atlaskit/icon/core/board';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import TableIcon from '@atlaskit/icon/core/table';
import { BUILTIN_TOOLBAR_KEYS, createEditorToolbarActions, type ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const DB_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('database');

const isDatabaseUrl = (url: string): boolean => {
	return url.match(/\/wiki\/spaces\/~?[\d\w]+\/database\/\d+(\?.*)?$/) !== null;
};

const dbIcons = [DatabaseIcon, TableIcon, GridIcon, BoardIcon];

/**
 * Experience manifest for a DB embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(DBExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const DBExperienceManifest: {
    readonly handleChildCommands: (_experience: Experience, _command: NativeEmbedChildCommand) => void;
    readonly id: ExperienceManifestId;
    readonly isActive: () => true;
    readonly isUrlMatch: (url: string) => boolean;
    readonly lockResizeAspectRatio: false;
    readonly parameterDefaults: {
        readonly alwaysShowTitle: true;
    };
    readonly shouldAutoConvertToNativeEmbed: true;
    readonly supportedFeatures: Set<string>;
    readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
    readonly uiConfig: {
        readonly appearance: "minimal";
        readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
        // eslint-disable-next-line @typescript-eslint/ban-types
        readonly editorToolbarHandlers: {};
        readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
        readonly showBorder: false;
        readonly showHeader: false;
        readonly titleIcon: {
            (props: NewCoreIconProps): JSX.Element;
            displayName: string;
        };
    };
} = {
	id: DB_EXPERIENCE_MANIFEST_ID,
	isUrlMatch: isDatabaseUrl,
	isActive: (): true => true,
	handleChildCommands: (_experience: Experience, _command: NativeEmbedChildCommand): void => {},
	parameterDefaults: {
		alwaysShowTitle: true,
	},
	shouldAutoConvertToNativeEmbed: true,
	lockResizeAspectRatio: false,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	transformUrl: (url: string, _isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('presentationMode', 'nativeEmbedEditorPlugin');
		return urlObj.toString();
	},
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				BUILTIN_TOOLBAR_KEYS.SUMMARISE,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.EDIT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={dbIcons}
				testId={props.testId}
			/>
		),
		titleIcon: DatabaseIcon,
		appearance: 'minimal',
		showHeader: false,
		showBorder: false,
	},
} as const satisfies ExperienceManifest;
